import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Density = 'compact' | 'default' | 'comfortable';
export type ColorScheme = 'light' | 'dark' | 'system';
export type Presence = 'online' | 'away' | 'busy' | 'offline';

interface UiState {
  sidebarCollapsed: boolean;
  mobileDrawerOpen: boolean;
  commandPaletteOpen: boolean;
  shortcutSheetOpen: boolean;
  density: Density;
  colorScheme: ColorScheme;
  presence: Presence;
  starredKeys: string[];
  recentPaths: string[];
  workspaceId: string;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileDrawerOpen: (open: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
  setShortcutSheetOpen: (open: boolean) => void;
  setDensity: (density: Density) => void;
  setColorScheme: (scheme: ColorScheme) => void;
  setPresence: (p: Presence) => void;
  toggleStar: (key: string) => void;
  pushRecent: (path: string) => void;
  setWorkspace: (id: string) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      mobileDrawerOpen: false,
      commandPaletteOpen: false,
      shortcutSheetOpen: false,
      density: 'default',
      colorScheme: 'light',
      presence: 'online',
      starredKeys: ['dashboard', 'articles'],
      recentPaths: [],
      workspaceId: 'emlak-katilim',
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setMobileDrawerOpen: (mobileDrawerOpen) => set({ mobileDrawerOpen }),
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
      toggleCommandPalette: () =>
        set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
      setShortcutSheetOpen: (shortcutSheetOpen) => set({ shortcutSheetOpen }),
      setDensity: (density) => set({ density }),
      setColorScheme: (colorScheme) => set({ colorScheme }),
      setPresence: (presence) => set({ presence }),
      toggleStar: (key) =>
        set((s) => ({
          starredKeys: s.starredKeys.includes(key)
            ? s.starredKeys.filter((k) => k !== key)
            : [...s.starredKeys, key],
        })),
      pushRecent: (path) =>
        set((s) => {
          const filtered = s.recentPaths.filter((p) => p !== path);
          return { recentPaths: [path, ...filtered].slice(0, 8) };
        }),
      setWorkspace: (workspaceId) => set({ workspaceId }),
    }),
    {
      name: 'emlak-cms.ui',
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        density: s.density,
        colorScheme: s.colorScheme,
        presence: s.presence,
        starredKeys: s.starredKeys,
        recentPaths: s.recentPaths,
        workspaceId: s.workspaceId,
      }),
    },
  ),
);

export function resolveIsDark(scheme: ColorScheme): boolean {
  if (scheme === 'dark') return true;
  if (scheme === 'light') return false;
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}
